import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

type SpinnerProps = {
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
  showLabel?: boolean;
};

const spinnerSizeClass: Record<NonNullable<SpinnerProps["size"]>, string> = {
  sm: "size-5",
  md: "size-8",
  lg: "size-16",
  xl: "size-24",
};

export const Spinner = ({
  className,
  size = "md",
  showLabel = true,
}: SpinnerProps) => {
  return (
    <>
      <svg
        aria-hidden="true"
        className={cn("animate-spin", spinnerSizeClass[size], className)}
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
      >
        <path
          fill="currentColor"
          stroke="currentColor"
          d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z"
          opacity={0.25}
        />
        <path
          fill="currentColor"
          stroke="currentColor"
          d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z"
        />
      </svg>
      {showLabel && <span className="sr-only">Loading</span>}
    </>
  );
};

type LoaderScreenProps = {
  className?: string;
  contained?: boolean;
  label?: ReactNode;
};

export const LoaderScreen = ({
  className,
  contained = false,
  label,
}: LoaderScreenProps) => {
  return (
    <div
      className={cn(
        "flex items-center justify-center text-rw-main",
        contained ? "size-full" : "h-screen w-screen",
        className,
      )}
    >
      <LoadingIndicator
        label={label}
        size="xl"
        labelClassName="mt-3 text-sm text-text-subtitle"
      />
    </div>
  );
};

type LoadingIndicatorProps = {
  className?: string;
  inline?: boolean;
  label?: ReactNode;
  labelClassName?: string;
  size?: SpinnerProps["size"];
};

export const LoadingIndicator = ({
  className,
  inline = false,
  label,
  labelClassName,
  size = "sm",
}: LoadingIndicatorProps) => {
  const Component = inline ? "span" : "div";

  return (
    <Component
      className={cn(
        inline
          ? "inline-flex items-center gap-2 align-middle"
          : "flex flex-col items-center justify-center",
        className,
      )}
    >
      <Spinner size={size} showLabel={!label} />
      {label && (
        <span
          className={cn(inline ? "text-sm" : "mt-2 text-sm", labelClassName)}
        >
          {label}
        </span>
      )}
    </Component>
  );
};
